import { Grid, Row, Spin, Tooltip } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AI_SUGGESTIONS_STATE_KEY, BOT_NAME, VOICE_CHARGEBEE_PLAN_MONTHLY, VOICE_CHARGEBEE_PLAN_YEARLY } from "../../constants/AppConstants";
import useAttachment from "../../hooks/useAttachment";
import AiAutoSuggestShowArea from "../../pages/AiAutoSuggestShowArea";
import SavedTemplatesSelect from "../../pages/templates/SavedTemplatesSelect";
import { checkUserHasEditAccess } from "../../pages/utils/commonUtils";
import { DropOverlay, FromNumberComponent } from "../../pages/utils/CommonVessels";
import ComponentPreview from "../custom/ComponentPreview";
import HTextArea from "../custom/input/textarea/HTextArea";
import HNotification from "../custom/notification/HNotification";
import ReusableFilePreview from "../custom/ReusableFilePreview";
import MessagingAreaActionItems from "./MessagingAreaActionItems";
import SendMessageButton from "./SendMessageButton";
import HButton from "../custom/input/button/Button";
import useContacts from "../../hooks/useContacts";

export default function CommonMessageTextArea({
  messageTextAreaContentRef,
  fileList,
  handleMediaFileList,
  appProperties,
  handleManageSubscription,
  rolesProperties,
  conversationContext,
  chatView,
  toNumberChatView,
  toNumber,
  setMessageAreaLoading,
  setTemplateId,
  templateId,
  handleMessage,
  handleMedia,
  setPreviewResponse,
  integId,
  resource,
  setShortcutTemplateOpen, // shortcut template open
  shortcutTemplateOpen, // shortcut template open
  previewResponse,
  messageAreaLoading,
  setConversationContext = () => { },
  contactId,
  messageTextAreaRowCount,
  message,
  unReadMessages,
  clearAllUnreadMessages,
  source,
  handleSendSMS,
  messageBodyRef,
  prompt,
  iconTemplateOpen, // template icon open
  setIconTemplateOpen, // template icon open
  handlePreviewResponse,
  handleOpenNotes,
  conversations,
  handleFromNumberChange,
  savedNumbersList,
  setSavedNumbersList = () => { },
  antIcon,
  sendBtnLoading,
  newFromNumber,
  moduleName,
  moduleNameForUserAccessCheck,
  hiddenItems,
  textAreaMinRows = 2,
  textAreaMaxRows = 20,
  filesPreviewPosition = "top",
  handlePreviewRemove,
  isContentPresent,
  showPreviewButton,
  toggleDrawer,
  enabledCheck,
  fetchAndShowTemplatePreview = true,
  messageAreaDataCy,
  disabled = false,
  previewParams,
  setNumbersList = () => { },
  shrinkView,
  disabledMessageArea,
  fetchAllTemplates = false,
  isRcsEnabled = false,
}) {
  const { handleUpdateConversationStatus, loadingStates } = useContacts(appProperties);
  const isConversationClosed = conversationContext?.[contactId?.current]?.conversationStatusType && conversationContext?.[contactId?.current]?.conversationStatusType === appProperties?.enums?.conversationStatusType?.CLOSED?.code;
  const cursorPositionRef = useRef(message?.length || 0);
  const handleCursorPosition = (event) => {
    cursorPositionRef.current = event.target.selectionStart || 0;
  };
  const handleReopenConversation = async (contactId) => {
    if (!contactId || source !== "conversation") return;
    handleUpdateConversationStatus(contactId, appProperties?.enums?.conversationStatus?.OPEN?.code, setNumbersList, setConversationContext);
  };
  // lg : greater than 992px
  const { lg: greaterThanLargeScreen } = Grid.useBreakpoint();

  useEffect(() => {
    appProperties?.controller?.isAppRunningOnDesktop?.().then((isDesktop) => {
      if (isDesktop) {
        messageBodyRef?.current?.focus();
      }
    });
  }, [appProperties?.controller, contactId?.current, messageBodyRef]);

  const dragOverlayRef = useRef(null);

  const [isAiGeneratedMessage, setIsAiGeneratedMessage] = useState(false);

  const getStoredExpandedState = () => {
    try {
      const storedState = window.localStorage.getItem(AI_SUGGESTIONS_STATE_KEY);
      return storedState !== null ? JSON.parse(storedState) : true; // Default to expanded (true)
    } catch (error) {
      console.error("Error reading AI suggestions state from localStorage:", error);
      return true; // Default to expanded on error
    }
  };

  const [isExpanded, setIsExpanded] = useState(() => getStoredExpandedState());

  const isCampaign = source === "campaign";

  const isTemplateNotSupported = useMemo(() => hiddenItems?.includes("select-template"), [hiddenItems]);

  const onMessageChange = useCallback(
    (...args) => {
      const messageText = args[0];
      const event = args[1];
      const messageSource = args[2];
      if (!isTemplateNotSupported) {
        if (!shortcutTemplateOpen && messageText?.trim?.() === "/") {
          setShortcutTemplateOpen(true);
        } else if (shortcutTemplateOpen) {
          setShortcutTemplateOpen(false);
        }
      }

      if (messageSource === "ai-reply") {
        setIsAiGeneratedMessage(true);
      } else if (isAiGeneratedMessage && (messageText === "" || messageSource === "templates")) {
        setIsAiGeneratedMessage(false);
      }
      handleMessage(messageText, event, ...(args?.slice?.(2) || []));
    },
    [handleMessage, isTemplateNotSupported, shortcutTemplateOpen, setShortcutTemplateOpen, isAiGeneratedMessage],
  );

  const processAndSendMessage = useCallback(
    (...args) => {
      if (isAiGeneratedMessage) {
        setIsAiGeneratedMessage(false);
      }
      handleSendSMS(...args);
    },
    [isAiGeneratedMessage, handleSendSMS],
  );

  function CommonSendMessageButton() {
    return (
      <SendMessageButton
        messageBodyRef={messageBodyRef}
        previewResponse={previewResponse}
        conversations={conversations}
        contactId={contactId}
        unReadMessages={conversations?.filter((msg) => msg?.isRead === false && msg?.direction === 1) || []}
        handleFromNumberChange={handleFromNumberChange}
        savedNumbersList={savedNumbersList}
        setSavedNumbersList={setSavedNumbersList}
        integId={integId}
        chatView={chatView}
        antIcon={antIcon}
        sendBtnLoading={sendBtnLoading}
        handleSendsms={processAndSendMessage}
        prompt={prompt}
        setMessageAreaLoading={setMessageAreaLoading}
        setMessage={setConversationContext}
        appProperties={appProperties}
        newFromNumber={newFromNumber}
      />
    );
  }

  const commonTemplateProps = useMemo(() => {
    return {
      templateId: templateId,
      setTemplateId: setTemplateId,
      showPreview: fetchAndShowTemplatePreview,
      previewParams: previewParams,
      fetchAllTemplates: fetchAllTemplates,
      placement: source === "automation" ? "bottomLeft" : undefined,
    };
  }, [templateId, setTemplateId, fetchAndShowTemplatePreview, previewParams, fetchAllTemplates, source]);

  const { xs } = Grid.useBreakpoint();

  // Drag and drop handlers - disabled for "conversation" source (handled separately)
  // For all other sources, use simple handlers similar to ConversationPage
  const isConversationSource = source === "conversation";
  const { uploadAttachment } = useAttachment(appProperties, fileList, handleMediaFileList, false);
  const [isDraggingOverAndUploadAllowed, setIsDraggingOverAndUploadAllowed] = useState(false);

  // Simple drag handlers for non-conversation sources
  const handleDragOver = useCallback(
    (e) => {
      if (isConversationSource) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.types.includes("Files")) {
        setIsDraggingOverAndUploadAllowed(true);
      }
    },
    [isConversationSource],
  );

  const handleDragLeave = useCallback(
    (e) => {
      if (isConversationSource || e.target !== dragOverlayRef?.current) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOverAndUploadAllowed(false);
    },
    [isConversationSource, dragOverlayRef],
  );

  const handleDrop = useCallback(
    async (e) => {
      if (isConversationSource) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOverAndUploadAllowed(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      const currentContactInfo = conversationContext?.[contactId?.current] || {};
      const currentIntegId = currentContactInfo?.integId || integId;
      if (!currentIntegId && source !== "add-template") {
        HNotification.notify({ type: "error", message: "MMS Alert", description: "From number is required to upload files" });
        return;
      }

      for (const file of files) {
        await uploadAttachment(file, null, null, null, null, currentIntegId, null, contactId?.current);
      }
    },
    [isConversationSource, conversationContext, contactId, integId, uploadAttachment],
  );

  const handleAiSuggestionAccept = useCallback(
    (suggestion) => {
      // Update message with accepted suggestion
      onMessageChange(suggestion, null, "ai-reply");
    },
    [onMessageChange],
  );

  return (
    <>
      <div
        ref={messageTextAreaContentRef}
        className="position-relative"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {!isConversationSource && <DropOverlay isDraggingOver={isDraggingOverAndUploadAllowed} dragOverlayRef={dragOverlayRef} />}
        <Row className="position-absolute w-100" style={{ bottom: "100%", left: 0, right: 0 }}>
          <div className="d-flex flex-column align-items-start w-100 gap-2 pt-2">
            {appProperties?.isAutoSuggestionEnabled && contactId?.current && (
              <AiAutoSuggestShowArea
                chatView={chatView}
                key={contactId.current}
                appProperties={appProperties}
                contactId={contactId.current}
                conversations={conversations}
                onSuggestionAccept={handleAiSuggestionAccept}
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
              />
            )}
            {fileList && fileList?.length > 0 && source !== "campaign" && filesPreviewPosition === "top" ? (
              <ReusableFilePreview
                fileList={fileList}
                setFileList={handleMediaFileList}
                shape={"circle"}
                direction={"horizontal"}
                customClasses={"ps-3"}
              />
            ) : null}
          </div>
        </Row>
        <div
          className={`h-100 hs-messagearea-border ${isCampaign || source === "add-template" || source === "automation" ? "hs-input-box" : "hs-messagearea-box-shadow"} ${
            isDraggingOverAndUploadAllowed ? "hs-file-upload-drag-over-bg" : ""
          } w-100 px-3 pt-2 d-flex flex-column justify-content-end position-relative overflow-visible`}
        >
          {/* Render collapsed icon inside textarea container - positioned behind textarea */}
          {source !== "playground" && appProperties?.isAutoSuggestionEnabled && contactId?.current && !isExpanded && (
            <Tooltip title={`Hey, it's ${BOT_NAME} here!`} placement="top">
              <div className="ai-suggestions-dot-wrapper">
                <div
                  className="actionIconsSprite ai-bot-icon"
                  onClick={() => {
                    setIsExpanded(true);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setIsExpanded(true);
                    }
                  }}
                />
              </div>
            </Tooltip>
          )}
          {previewResponse?.components && source !== "campaign" ? (
            <>
              <ComponentPreview
                handleRemove={handlePreviewRemove}
                componentsData={previewResponse?.components}
                chatView={chatView}
                source={source}
              />
              {handleSendSMS && (
                <div className="ms-auto">
                  <CommonSendMessageButton />
                </div>
              )}
            </>
          ) : (
            <>
              {[VOICE_CHARGEBEE_PLAN_MONTHLY, VOICE_CHARGEBEE_PLAN_YEARLY].includes(appProperties?.licenseObj?.licenseDetails?.planName) ? (
                <div className="d-flex justify-content-between align-items-center px-2 pb-2">
                  <div className="hs-bg-light-gray w-100 p-2 hs-border-8 mx-2 hs-border-md-grey d-flex align-items-center justify-content-between">
                    <div className="hs-fs-14 hs-color-md-grey hs-fw-400">Upgrade to PRO to unlock messaging features</div>
                    <div
                      className="d-flex flex-row hs-fs-14 hs-fw-500 align-items-center hs-color-violet cursor-pointer"
                      onClick={handleManageSubscription}
                    >
                      Upgrade
                      <div className="call-action-icons go-to-arrow-violet" />
                    </div>
                  </div>
                </div>
              ) : !checkUserHasEditAccess(rolesProperties?.currentRoleDetails, moduleNameForUserAccessCheck) ? (
                <div className="d-flex justify-content-between align-items-center px-2 pb-2">
                  <div className="hs-bg-light-gray w-100 p-2 hs-border-8 mx-2 hs-border-md-grey d-flex align-items-center justify-content-between">
                    <div className="hs-fs-14 hs-color-md-grey hs-fw-400">
                      You do not have permission to send messages. You have view-only access
                    </div>
                  </div>
                </div>
              ) : isConversationClosed ? (
                <>
                  <div className="d-flex justify-content-center align-items-center w-100">
                    <div className="w-100 p-2 mx-2 d-flex flex-column align-items-center justify-content-between gap-2">
                      <div className="hs-fs-12 hs-color-light-grey-2 hs-fw-400">The conversation is closed</div>
                      <HButton
                        size="small"
                        onClick={() => handleReopenConversation(contactId?.current)}
                        loading={loadingStates[contactId?.current]?.changeStatus}
                        icon={<div className="actionIconsSprite2 conversation-open-white-icon" />}
                      >
                        Reopen Conversation
                      </HButton>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {isTemplateNotSupported ? null : (
                    <div className="position-relative">
                      <SavedTemplatesSelect
                        {...commonTemplateProps}
                        isRcsEnabled={isRcsEnabled}
                        open={shortcutTemplateOpen}
                        setOpen={setShortcutTemplateOpen}
                        handleMessage={(value, e, source = "templates") => onMessageChange(value, e, source)}
                        setPreviewResponse={handlePreviewResponse}
                        toNumber={chatView ? toNumberChatView : toNumber}
                        appProperties={appProperties}
                        moduleName={moduleName}
                        handleMedia={handleMedia}
                        chatView={chatView}
                        integrationId={integId || conversationContext?.[contactId?.current]?.integId}
                        setMessageAreaLoading={setMessageAreaLoading}
                        placement={source === "automation" ? "bottomLeft" : "topRight"}
                        showOnlyPopup={true}
                        dropdownStyle={chatView || xs ? { width: 200 } : { width: 300 }}
                        shrinkView={shrinkView}
                      />
                    </div>
                  )}
                  <div className={`${isAiGeneratedMessage ? "ai-chat-container" : ""}`}>
                    {isAiGeneratedMessage && (
                      <div className="ai-chat-badge d-flex align-items-center">
                        <div className="actionIconsSprite ai-chat-icon" />
                        <div>Generated with AI assistance</div>
                      </div>
                    )}
                    <Spin spinning={messageAreaLoading}>
                      <HTextArea
                        autoSize={{
                          minRows: textAreaMinRows,
                          maxRows: chatView ? 16 : textAreaMaxRows,
                        }}
                        id={contactId?.current || crypto.randomUUID()}
                        variant="borderless"
                        rows={messageTextAreaRowCount}
                        data-cy={messageAreaDataCy}
                        placeholder={
                          source === "playground"
                            ? "Type here to get started"
                            : isTemplateNotSupported
                              ? "Enter your message here"
                              : "Type '/' to open Templates"
                        }
                        // Removed rootClassName="ms-2" as margin is now on the wrapper
                        value={message}
                        disabled={disabled || previewResponse?.components}
                        onChange={(e) => {
                          onMessageChange(e?.target?.value, e, "messageArea");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey && (source === "conversation" || source === "playground")) {
                            e.preventDefault();
                            processAndSendMessage();
                          } else {
                            handleCursorPosition(e);
                          }
                        }}
                        onClick={(e) => {
                          handleCursorPosition(e);
                          if (unReadMessages?.length > 0 && source === "conversation") {
                            clearAllUnreadMessages();
                          }
                        }}
                        ref={messageBodyRef}
                        // Added ps-0 to remove default padding if needed, adjust HTextArea internal padding if necessary
                        className={`conv-textArea p-0 ${!greaterThanLargeScreen ? "hs-fs-16" : ""}`}
                      />
                    </Spin>
                  </div>
                  <div className="d-flex justify-content-between align-items-center w-100 mb-1">
                    {source !== "playground" && (
                      <div className="w-100">
                        <MessagingAreaActionItems
                          templateProps={{
                            ...commonTemplateProps,
                            open: iconTemplateOpen,
                            setOpen: setIconTemplateOpen,
                            isRcsEnabled,
                          }}
                          disabled={disabled}
                          message={message}
                          cursorPositionRef={cursorPositionRef}
                          prompt={prompt}
                          messageBodyRef={messageBodyRef}
                          appProperties={appProperties}
                          handleMessage={(value, e, source = "templates") => onMessageChange(value, e, source)}
                          setMessageAreaLoading={setMessageAreaLoading}
                          iconsSize={chatView ? "small" : "large"}
                          refetchOnModuleChange={true}
                          toNumber={chatView ? toNumberChatView : toNumber}
                          showOnlyPopup={false}
                          integrationId={integId || conversationContext?.[contactId?.current]?.integId}
                          setLoading={null}
                          handleMedia={handleMedia}
                          loadTemplates={true}
                          moduleName={moduleName}
                          chatView={chatView}
                          groupBy={"moduleName"}
                          fileList={fileList}
                          setFileList={handleMediaFileList}
                          contactId={contactId?.current}
                          source={source}
                          previewResponse={previewResponse}
                          setPreviewResponse={handlePreviewResponse}
                          handleOpenNotes={handleOpenNotes}
                          hiddenItems={hiddenItems}
                          filesPreviewPosition={filesPreviewPosition}
                          isContentPresent={isContentPresent}
                          showPreviewButton={showPreviewButton}
                          toggleDrawer={toggleDrawer}
                          enabledCheck={enabledCheck}
                        />
                      </div>
                    )}
                    {handleSendSMS ? (
                      <div className={`d-flex align-items-center gap-2 ${source === "playground" ? "ms-auto" : ""}`}>
                        {(conversations?.length < 1 || !integId) && !chatView && source !== "playground" && (
                          <div>
                            <FromNumberComponent
                              customClasses={"fromNumber-conv"}
                              fromNumber={newFromNumber}
                              handleFromNumberChange={handleFromNumberChange}
                              savedNumbersList={savedNumbersList}
                              setSavedNumbersList={setSavedNumbersList}
                              disabled={false}
                            />
                          </div>
                        )}
                        <CommonSendMessageButton />
                      </div>
                    ) : null}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
